import type { CSSProperties, ReactNode } from 'react';
import Box from '@mui/material/Box';
import { LAYOUT } from '../theme/tokens';

/**
 * A column. `width` is a PERCENTAGE, because the table is `table-layout: fixed` and
 * sized entirely by its `<colgroup>` — the widths must total 100%.
 */
export interface ScrollTableColumn<R> {
  key: string;
  label: string;
  /** e.g. '19%'. All columns together must add up to 100%. */
  width: string;
  align?: CSSProperties['textAlign'];
  /** Header alignment, when it differs from the body's. */
  headerAlign?: CSSProperties['textAlign'];
  render: (row: R) => ReactNode;
  /** Per-cell extras — APR puts the description in a `title` for a hover tooltip. */
  cellProps?: (row: R) => { title?: string; style?: CSSProperties };
}

export interface ScrollTableProps<R> {
  rows: readonly R[];
  columns: readonly ScrollTableColumn<R>[];
  getRowId: (row: R) => string;
  /** Container height. Tracks the viewport so the table grows with the window. */
  height?: string;
  onRowClick?: (row: R) => void;
  /** Row tinting — parent, child and selected rows each have their own. */
  getRowStyle?: (row: R) => CSSProperties | undefined;
  /** Extra row attributes, e.g. `data-app-row` so a deep link can scroll to a row. */
  getRowAttributes?: (row: R) => Record<string, string>;
}

/**
 * The dense data table, ported from APR.
 *
 * WHY THIS EXISTS ALONGSIDE `DataTable`
 * -------------------------------------
 * `DataTable` wraps MUI X DataGrid **Community**, which caps a page at 100 rows and
 * rejects `pagination={false}` — that is a Pro feature. A 576-row inventory could
 * therefore never be shown the way APR shows it, and the attempt to force it silently
 * hid 476 rows behind a suppressed pager. APR paginates nothing: every row is on one
 * scrolling surface, and that is the behaviour being matched.
 *
 * So this renders a plain table with every row in the DOM — exactly as APR does, at
 * the same row count, which is the proof it performs. It is deliberately NOT a
 * general-purpose grid: no pagination, no column sorting, no resizing. APR's headers
 * do none of those either (its row order is fixed: parents by application ID with
 * their children grouped beneath), so nothing is lost by leaving them out. Reach for
 * `DataTable` on screens that want a grid; reach for this one to match APR.
 *
 * STRUCTURE — two tables, not one
 * -------------------------------
 * The header is its own table outside the scrolling box, and the body is a second
 * table inside it. Both are `table-layout: fixed` over an identical `<colgroup>`, so
 * the columns line up while only the body scrolls. APR does this because a sticky
 * `<th>` does not work in a `border-collapse: collapse` table in Safari (WebKit) —
 * its own CSS says so at App.css:5287, where the headers on these very screens
 * rendered invisible. A separate header table is fixed by construction instead.
 *
 * Measurements are APR's, read from `apr_template/src/App.css` (`.master-data-table`,
 * line 5267) and the inline header styles on both screens — not estimated.
 */

/**
 * `.master-data-table th, td` — App.css:5273.
 *
 * Declared ONCE on the table and inherited by every cell through a descendant selector,
 * which is how the source does it. The first version put this on each cell as an `sx`
 * prop: 579 rows x 10 columns is 5,790 MUI `Box` components, each resolving its own
 * emotion style on every render, against the source's 5,790 bare `<td>`s carrying no
 * class at all. That is the whole performance difference — the row count is identical.
 */
const CELL_RULES = {
  '& th, & td': {
    padding: '0.45rem 0.5rem',
    textAlign: 'left',
    borderBottom: '1px solid #dee2e6',
    fontSize: '13px',
    verticalAlign: 'middle',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
  },
} as const;

/**
 * Both screens override the stylesheet's lighter header inline with this darker one,
 * so it is the real header and belongs here rather than in either screen.
 */
const HEADER_RULES = {
  '& th': {
    backgroundColor: '#e8e8e8',
    borderBottom: '2px solid #bbb',
    padding: '10px 8px',
    fontWeight: 700,
    fontSize: '13px',
    color: '#333',
  },
} as const;

/** `.master-data-table` — App.css:5267. The 15px is the table's base; cells override to 13px. */
const TABLE_SX = {
  tableLayout: 'fixed',
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '15px',
  color: '#333',
} as const;

export function ScrollTable<R>({
  rows,
  columns,
  getRowId,
  height = `calc(100vh - 300px - ${LAYOUT.footerHeight}px)`,
  onRowClick,
  getRowStyle,
  getRowAttributes,
}: ScrollTableProps<R>) {
  // One definition, rendered into both tables — the two colgroups cannot drift apart
  // and take the columns out of alignment.
  const colgroup = (
    <colgroup>
      {columns.map((column) => (
        <col key={column.key} style={{ width: column.width }} />
      ))}
    </colgroup>
  );

  return (
    <Box
      sx={{
        border: '1px solid #dee2e6',
        borderRadius: '0.5rem',
        backgroundColor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        height,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Box component="table" sx={{ ...TABLE_SX, ...CELL_RULES, ...HEADER_RULES }}>
          {colgroup}
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  style={{ textAlign: column.headerAlign ?? column.align ?? 'left' }}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        <Box
          component="table"
          sx={{
            ...TABLE_SX,
            ...CELL_RULES,
            // APR: the whole row lifts on hover, which is how a 579-row table stays
            // readable without zebra striping.
            '& tbody tr:hover': { backgroundColor: '#f8f9fa' },
          }}
        >
          {colgroup}
          <tbody>
            {rows.map((row) => {
              const id = getRowId(row);
              return (
                // Row tinting goes on `style`, NOT `sx`. APR sets it inline, so it beats
                // the stylesheet's `tbody tr:hover` — a shaded parent or child row keeps
                // its colour under the cursor rather than flashing to the hover grey.
                // Emitting it as a class would lose that, and the family blocks would
                // break up on hover.
                <tr
                  key={id}
                  {...(getRowAttributes?.(row) ?? {})}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  style={{
                    ...(onRowClick ? { cursor: 'pointer' } : {}),
                    ...(getRowStyle?.(row) ?? {}),
                  }}
                >
                  {columns.map((column) => {
                    const extra = column.cellProps?.(row) ?? {};
                    return (
                      <td
                        key={column.key}
                        {...(extra.title !== undefined ? { title: extra.title } : {})}
                        style={{
                          ...(column.align ? { textAlign: column.align } : {}),
                          ...(extra.style ?? {}),
                        }}
                      >
                        {column.render(row)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Box>
      </Box>
    </Box>
  );
}
